import React, { useCallback, useState } from 'react';
import { Center, Group, Text } from '@mantine/core';
import { Actions, Layout } from 'flexlayout-react';
import type { Action, Model, TabNode, TabSetNode } from 'flexlayout-react';
import UrlViewer from '@/pages/Result/components/UrlViewer.tsx';
import { useArrayQueryParam } from '@/hooks/useArrayQueryParam.ts';
import { useDatasetsCacheDict } from '@/pages/Result/hooks/useDatasetsDict.ts';
import { isDatasetId } from '@/pages/Result/utils.ts';
import DatasetIcon from '@/components/DatasetIcon.tsx';
import CustomSpinner from '@/components/Spinner.tsx';
import { getDisplayDomain, getFaviconUrl } from '@/utils/urlDisplay.ts';
import ResultPageDataViewer from '@/pages/Result/components/ResultPageDataViewer.tsx';

type TabsLayoutProps = {
  model: Model;
};

const getItemFromTab = (tab: TabNode) => ((tab.getConfig() || {}) as { item: string }).item;

const TabsLayout = ({ model }: TabsLayoutProps) => {
  const { items, setItems } = useArrayQueryParam('datasetIds');

  const [allTabs, setAllTabs] = useState<string[]>(items);

  const datasetsData = useDatasetsCacheDict(allTabs.filter((t) => isDatasetId(t)));

  const handleModelChange = useCallback(
    async (m: Model, a: Action) => {
      if (a.type === Actions.UPDATE_MODEL_ATTRIBUTES) return;

      const active: string[] = [];
      const all: string[] = [];

      m.visitNodes((node) => {
        if (node.getType() === 'tabset') {
          const selected = (node as TabSetNode).getSelectedNode() as TabNode | undefined;
          if (selected) active.push(getItemFromTab(selected));
        } else if (node.getType() === 'tab') {
          all.push(getItemFromTab(node as TabNode));
        }
      });
      await setItems(active);
      setAllTabs(all);

      m.doAction(Actions.updateModelAttributes({ tabEnableDrag: all.length !== 1 }));
    },
    [setItems],
  );

  const factory = useCallback((node: TabNode) => {
    const component = node.getComponent();
    const item = getItemFromTab(node);

    if (component === 'dataset') {
      return <ResultPageDataViewer datasetId={item} />;
    } else {
      return <UrlViewer item={item} />;
    }
  }, []);

  return (
    <Layout
      model={model}
      factory={factory}
      onRenderTab={(node, renderValues) => {
        // renderValues.content is the tab label
        const item = getItemFromTab(node);

        if (isDatasetId(item)) {
          const dataset = item in datasetsData ? datasetsData[item] : undefined;
          if (dataset) {
            renderValues.content = (
              <Group gap={'6px'} wrap={'nowrap'}>
                <DatasetIcon dataset={dataset} size={'16px'} />
                <Text size="12px" truncate={'end'} style={{ minWidth: 0, maxWidth: '150px' }}>
                  {dataset.name}
                </Text>
              </Group>
            );
          } else {
            renderValues.content = (
              <Center w={'100%'} h={'100%'}>
                <CustomSpinner size={'xs'} />
              </Center>
            );
          }
        } else {
          renderValues.content = (
            <Group gap={0} wrap="nowrap">
              <img
                src={getFaviconUrl(item)}
                alt="favicon"
                style={{
                  maxHeight: '12px',
                  maxWidth: '12px',
                  objectFit: 'contain',
                  flexShrink: 0,
                  borderRadius: '50%',
                }}
              />
              <Text size="10px" truncate={'end'}>
                {getDisplayDomain(item)}
              </Text>
            </Group>
          );
        }
      }}
      onModelChange={handleModelChange}
      realtimeResize
    />
  );
};

export default TabsLayout;
